import { useState } from 'react';
import { Button, Dialog, Field, Icon, Input, useErrorDialog, useToast } from '@dayon/design-system';

import { api, ApiRequestError } from '../../lib/api.ts';
import { describeModel, type AffiliateRow } from './types.ts';

/**
 * Approve a pending affiliate.
 *
 * "A tracking link and sub-ID prefix are issued on creation; commission starts
 * on approval." So this dialog is the moment attribution begins, and it says so
 * in terms of the affiliate's OWN plan rather than in the abstract — a dialog
 * naming the wrong commission basis is a dialog an operator approves money
 * under.
 *
 * Two refusals are pre-empted here so the operator reads them as rules rather
 * than as errors after the fact:
 *
 *   - an affiliate with NO LIVE PLAN cannot be approved at all, because
 *     attribution with no commission basis makes every player it sends
 *     unattributable to a payout;
 *   - the person who FILED it cannot approve it. The database enforces that
 *     with `affiliate_approver_is_not_creator`; the API refuses first, and the
 *     refusal arrives here as the sentence, not as a constraint name.
 */
export function ApproveAffiliateDialog({
  affiliate,
  onClose,
  onDone,
}: {
  affiliate: AffiliateRow | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const { showError } = useErrorDialog();
  const { toast } = useToast();
  const [note, setNote] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const close = () => {
    setNote('');
    setFields({});
    onClose();
  };

  const submit = async () => {
    if (affiliate === null) return;
    setBusy(true);
    try {
      const result = await api.post<{ ref: string; plan: string; message: string }>(
        `/api/admin/affiliates/${affiliate.id}/approve`,
        { note },
      );
      toast(result.message);
      close();
      onDone();
    } catch (error: unknown) {
      // A field problem stays inline; a refusal is a decision that did NOT
      // happen, and that goes to the modal.
      if (error instanceof ApiRequestError && error.fields) {
        setFields(error.fields);
        return;
      }
      showError({
        title: `Could not approve ${affiliate.ref}`,
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'The approval was not recorded. Try again shortly.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setBusy(false);
    }
  };

  const noPlan = affiliate !== null && affiliate.model === null;

  return (
    <Dialog
      open={affiliate !== null}
      onClose={close}
      title={affiliate === null ? '' : `Approve ${affiliate.ref}?`}
      actions={
        <>
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button
            variant="primary"
            busy={busy}
            // Not a styling choice: approving without a plan is the one outcome
            // the API refuses outright, so the control does not offer it.
            disabled={noPlan}
            onClick={() => void submit()}
          >
            Approve
          </Button>
        </>
      }
    >
      {affiliate && (
        <div className="stack">
          <p style={{ margin: 0 }}>
            A tracking link and sub-ID prefix are issued on creation; commission starts on
            approval. Approving {affiliate.name} puts it on the{' '}
            {describeModel(affiliate.model, affiliate.revshare_bp, affiliate.cpa_amount)} plan and
            begins attributing players to it from that moment.
          </p>

          {noPlan && (
            <div className="notice">
              <Icon name="warning-circle" />
              <div>
                <strong>This affiliate has no live plan.</strong> Approving it would start
                attribution with no commission basis, so every player it sends would be
                unattributable to a payout. Set a commission model on it first — the affiliate
                drawer has the control.
              </div>
            </div>
          )}

          <Field
            label="Why this is being approved"
            error={fields['note']}
            hint="Recorded in the audit trail beside your name. The affiliate does not see it."
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                value={note}
                onChange={(e) => {
                  setNote(e.target.value);
                  setFields({});
                }}
                placeholder="KYB documents seen, terms countersigned"
              />
            )}
          </Field>

          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              Whoever filed an affiliate cannot approve it. If you added this one, a second admin
              holding <code>partners.manage</code> has to make this decision — the platform refuses
              it either way.
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
}
